import { AssignedRule, InterferenceRule, InterferenceRuleInput } from '../../@types/rules';
import { API_BASE_URL } from '../../apiConfig';
import { ApiFacade } from './apiFacade';

const RULES_URL = `${API_BASE_URL}/api/v1/rules`;
const PROJECTS_URL = `${API_BASE_URL}/api/v1/projects`;
const JSON_FETCH_OPTIONS: RequestInit = { headers: { 'Content-Type': 'application/json' } };

type RuleListResponse = InterferenceRule[] | { rules: InterferenceRule[] };

export type AccessTokenProvider = () => Promise<string | null>;

export interface RulesApi {
    getRules(): Promise<InterferenceRule[]>;
    getActiveRules(): Promise<InterferenceRule[]>;
    getRule(ruleId: string): Promise<InterferenceRule>;
    createInterferenceRule(input: InterferenceRuleInput): Promise<InterferenceRule>;
    updateInterferenceRule(ruleId: string, input: InterferenceRuleInput): Promise<InterferenceRule>;
    activateRule(ruleId: string): Promise<void>;
    deactivateRule(ruleId: string): Promise<void>;
    deleteRule(ruleId: string): Promise<void>;
    getProjectRules(projectId: string): Promise<AssignedRule[]>;
    assignRuleToProject(projectId: string, ruleId: string): Promise<void>;
    unassignRuleFromProject(projectId: string, ruleId: string): Promise<void>;
}

function encodeRuleId(ruleId: string): string {
    return encodeURIComponent(ruleId);
}

function projectRuleAssignmentUrl(projectId: string, ruleId: string): string {
    return `${PROJECTS_URL}/${encodeURIComponent(projectId)}/rules/${encodeRuleId(ruleId)}/assignment`;
}

export function createRulesApi(getAccessToken: AccessTokenProvider): RulesApi {
    async function withAccessToken<T>(request: (accessToken: string) => Promise<T>): Promise<T> {
        const accessToken = await getAccessToken();
        if (!accessToken) {
            const error = new Error('Authentication is required to manage rules.');
            (error as any).status = 401;
            throw error;
        }
        return request(accessToken);
    }

    async function getRuleList(url: string): Promise<InterferenceRule[]> {
        const response = await withAccessToken((accessToken) =>
            ApiFacade.getJson<RuleListResponse>(url, { accessToken, fetchOptions: JSON_FETCH_OPTIONS })
        );
        if (Array.isArray(response)) return response;
        if (response && Array.isArray(response.rules)) return response.rules;
        throw new Error('Invalid rules list response.');
    }

    return {
        getRules: () => getRuleList(RULES_URL),

        getActiveRules: () => getRuleList(`${RULES_URL}?status=active`),

        getRule: (ruleId) => withAccessToken((accessToken) =>
            ApiFacade.getJson<InterferenceRule>(`${RULES_URL}/${encodeRuleId(ruleId)}`, { accessToken, fetchOptions: JSON_FETCH_OPTIONS })
        ),

        createInterferenceRule: (input) => withAccessToken((accessToken) =>
            ApiFacade.postJson<InterferenceRule>(`${RULES_URL}/interference`, {
                accessToken,
                body: input,
            })
        ),

        updateInterferenceRule: (ruleId, input) => withAccessToken((accessToken) =>
            ApiFacade.patchJson<InterferenceRule>(`${RULES_URL}/interference/${encodeRuleId(ruleId)}`, {
                accessToken,
                body: input,
            })
        ),

        activateRule: async (ruleId) => {
            await withAccessToken((accessToken) =>
                ApiFacade.postJson(`${RULES_URL}/${encodeRuleId(ruleId)}/org-assignment`, {
                    accessToken,
                    fetchOptions: JSON_FETCH_OPTIONS,
                })
            );
        },

        deactivateRule: async (ruleId) => {
            await withAccessToken((accessToken) =>
                ApiFacade.deleteJson(`${RULES_URL}/${encodeRuleId(ruleId)}/org-assignment`, {
                    accessToken,
                    fetchOptions: JSON_FETCH_OPTIONS,
                })
            );
        },

        deleteRule: async (ruleId) => {
            await withAccessToken((accessToken) =>
                ApiFacade.deleteJson(`${RULES_URL}/${encodeRuleId(ruleId)}`, {
                    accessToken,
                    fetchOptions: JSON_FETCH_OPTIONS,
                })
            );
        },

        getProjectRules: (projectId) => withAccessToken(async (accessToken) => {
            const response = await ApiFacade.getJson<{ rules: AssignedRule[] }>(
                `${PROJECTS_URL}/${encodeURIComponent(projectId)}/rules`,
                { accessToken, fetchOptions: JSON_FETCH_OPTIONS }
            );
            if (!response || !Array.isArray(response.rules)) {
                throw new Error('Invalid project rules response.');
            }
            return response.rules;
        }),

        assignRuleToProject: async (projectId, ruleId) => {
            await withAccessToken((accessToken) =>
                ApiFacade.postJson(projectRuleAssignmentUrl(projectId, ruleId), {
                    accessToken,
                    fetchOptions: JSON_FETCH_OPTIONS,
                })
            );
        },

        unassignRuleFromProject: async (projectId, ruleId) => {
            await withAccessToken((accessToken) =>
                ApiFacade.deleteJson(projectRuleAssignmentUrl(projectId, ruleId), {
                    accessToken,
                    fetchOptions: JSON_FETCH_OPTIONS,
                })
            );
        },
    };
}
